/**
 * useAuthorityManagement Hook
 * 권한 설정 관련 로직 관리
 */
import { useState, useEffect, useCallback } from 'react';
import { authorityApi } from '../api/authorityApi';
import {
    Authority,
    AuthorityFormData,
    isStandardPermission,
} from '../types/authority.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { getItem } from '@/shared/utils/storage';

/**
 * 권한 설정 관리 훅
 */
export const useAuthorityManagement = () => {
    const { showToast } = useToast();

    // 로딩 상태
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // 데이터 상태
    const [authorities, setAuthorities] = useState<Authority[]>([]);

    /**
     * 권한 목록 조회
     * @param includeStandard 표준 권한 포함 여부
     */
    const fetchAuthorities = useCallback(async (includeStandard = true) => {
        try {
            setLoading(true);
            const data = await authorityApi.getAuthorityList(includeStandard);
            setAuthorities(data);
        } catch (error) {
            console.error('권한 목록 조회 실패:', error);
            showToast('권한 목록 조회에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    /**
     * 권한 생성
     */
    const createAuthority = useCallback(async (data: AuthorityFormData): Promise<boolean> => {
        try {
            setSaving(true);
            const userId = getItem<string>('USER_ID') || '';

            const response = await authorityApi.createAuthority({
                prmCode: data.prmCode,
                prmName: data.prmName,
                prmDesc: data.prmDesc,
                baseUserType: data.baseUserType,
                accessPages: data.accessPages,
                insertUserId: userId,
            });

            if (response.success) {
                showToast('권한이 등록되었습니다.', { type: ToastType.SUCCESS });
                await fetchAuthorities();
                return true;
            } else {
                showToast(response.message || '권한 등록에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch (error) {
            showToast('권한 등록에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setSaving(false);
        }
    }, [showToast, fetchAuthorities]);

    /**
     * 권한 수정
     */
    const updateAuthority = useCallback(async (data: AuthorityFormData): Promise<boolean> => {
        try {
            // 표준 권한은 수정 불가
            if (isStandardPermission(data.prmCode)) {
                showToast('표준 권한은 수정할 수 없습니다.', { type: ToastType.ERROR });
                return false;
            }

            setSaving(true);
            const userId = getItem<string>('USER_ID') || '';

            const response = await authorityApi.updateAuthority({
                prmCode: data.prmCode,
                prmName: data.prmName,
                prmDesc: data.prmDesc,
                baseUserType: data.baseUserType,
                accessPages: data.accessPages,
                updateUserId: userId,
            });

            if (response.success) {
                showToast('권한이 수정되었습니다.', { type: ToastType.SUCCESS });
                await fetchAuthorities();
                return true;
            } else {
                showToast(response.message || '권한 수정에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch (error) {
            showToast('권한 수정에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setSaving(false);
        }
    }, [showToast, fetchAuthorities]);

    /**
     * 권한 삭제
     */
    const deleteAuthority = useCallback(async (prmCode: string): Promise<boolean> => {
        try {
            // 표준 권한은 삭제 불가
            if (isStandardPermission(prmCode)) {
                showToast('표준 권한은 삭제할 수 없습니다.', { type: ToastType.ERROR });
                return false;
            }

            setSaving(true);
            const userId = getItem<string>('USER_ID') || '';

            const response = await authorityApi.deleteAuthority(prmCode, userId);

            if (response.success) {
                showToast('권한이 삭제되었습니다.', { type: ToastType.SUCCESS });
                await fetchAuthorities();
                return true;
            } else {
                showToast(response.message || '권한 삭제에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch (error) {
            showToast('권한 삭제에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setSaving(false);
        }
    }, [showToast, fetchAuthorities]);

    // 컴포넌트 마운트 시 권한 목록 조회
    useEffect(() => {
        fetchAuthorities();
    }, [fetchAuthorities]);

    return {
        // 상태
        loading,
        saving,
        authorities,

        // 함수
        fetchAuthorities,
        createAuthority,
        updateAuthority,
        deleteAuthority,
    };
};
